import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Visibility from '@mui/icons-material/VisibilityOutlined'
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined'
import { Button, Input } from '@/design-system'
import {
  authFieldSx,
  authPasswordFieldSx,
  ErrorBanner,
  FieldError,
  FieldLabel,
  FieldWrap,
  FormPage,
  FormSub,
  FormTitle,
  FormWrap,
  PasswordFieldBox,
  PasswordToggle,
  SwapRow,
} from './__styled'
import { validateEmail, validatePassword } from '@/utils/helper'

interface FormProps {
  error: string
  loading: boolean
  user: string
  onSubmit: (code: string, newPassword: string) => void
}

export default function ForgotForm2({ error, loading, user, onSubmit }: FormProps) {
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [editing, setEditing] = useState(false)

  const formError = useMemo(() => {
    const error: Record<string, string> = {}
    if (!code.length) {
      error.code = 'Verification code is required'
    } else if (code.length !== 6) {
      error.code = 'Code should be 6 characters exactly.'
    }
    if (!password.length) {
      error.password = 'Password is required'
    } else if (password.length < 8) {
      error.password = 'Password is too short - should be 8 chars minimum.'
    } else if (!validatePassword(password)) {
      error.password =
        'Password can only contain Latin Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character.'
    }
    if (password !== confirmPassword) {
      error.confirm = 'Passwords must match'
    }
    return error
  }, [code, password, confirmPassword])

  const isFormValid = !formError.code && !formError.password && !formError.confirm

  const onChangeCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value)
    setEditing(true)
  }

  const onChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    setEditing(true)
  }

  const onChangeConfirmPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value)
    setEditing(true)
  }

  const onConfirm = () => {
    if (isFormValid) onSubmit(code, password)
  }

  return (
    <FormPage>
      <FormWrap>
        <FormTitle>Confirmation code</FormTitle>
        <FormSub>
          Enter the code sent to your {validateEmail(user) ? 'email' : 'phone'} —{' '}
          <b>{user}</b>
        </FormSub>

        <FieldWrap>
          <FieldLabel>Confirmation code</FieldLabel>
          <Input
            variant='filled'
            sx={authFieldSx}
            value={code}
            placeholder='6-digit code'
            onChange={onChangeCode}
            name='code'
          />
          {editing && !!formError.code && <FieldError>{formError.code}</FieldError>}
        </FieldWrap>

        <FieldWrap>
          <FieldLabel>New password</FieldLabel>
          <PasswordFieldBox>
            <Input
              variant='filled'
              sx={authPasswordFieldSx}
              value={password}
              placeholder='At least 8 characters'
              onChange={onChangePassword}
              type={showPassword ? 'text' : 'password'}
              autoComplete='off'
            />
            <PasswordToggle type='button' onClick={() => setShowPassword((v) => !v)}>
              {showPassword ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
            </PasswordToggle>
          </PasswordFieldBox>
          {editing && !!formError.password && <FieldError>{formError.password}</FieldError>}
        </FieldWrap>

        <FieldWrap>
          <FieldLabel>Confirm password</FieldLabel>
          <PasswordFieldBox>
            <Input
              variant='filled'
              sx={authPasswordFieldSx}
              value={confirmPassword}
              placeholder='Type it again'
              onChange={onChangeConfirmPassword}
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete='off'
            />
            <PasswordToggle type='button' onClick={() => setShowConfirmPassword((v) => !v)}>
              {showConfirmPassword ? (
                <VisibilityOff fontSize='small' />
              ) : (
                <Visibility fontSize='small' />
              )}
            </PasswordToggle>
          </PasswordFieldBox>
          {editing && !!formError.confirm && <FieldError>{formError.confirm}</FieldError>}
        </FieldWrap>

        {!loading && !!error && (
          <ErrorBanner>
            <FieldError sx={{ mt: 0 }}>{error}</FieldError>
          </ErrorBanner>
        )}

        <Button
          variant='spectrum'
          size='lg'
          fullWidth
          disabled={!isFormValid}
          loading={loading}
          onClick={onConfirm}
        >
          Confirm →
        </Button>

        <SwapRow>
          Already have an account? <Link to='/login'>Sign in</Link>
        </SwapRow>
      </FormWrap>
    </FormPage>
  )
}
